import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { HeaderConfigService, OnDestroyBase, TrackingService } from '@os-components';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ConnectedArticleListComponent } from '../../components/connected-article-list/connected-article-list.component';
import { ContentComponent } from '../../components/content/content.component';
import { FsAddSectionComponent } from '../../components/fs/fs-add-section/fs-add-section.component';
import { FsService } from '../../components/fs/fs.service';
import { HeroPictureComponent } from '../../components/hero-picture/hero-picture.component';
import { getBreadcrumbs } from '../../helpers/breadcrumbs.helper';
import { CustomBreakpoint, getCustomBreakpoint, mediaQueries } from '../../helpers/breakpoint.helper';
import { tracking } from '../../helpers/tracking';
import { Author } from '../../models/author.model';
import { BlogPost } from '../../models/blog-posts.model';
import { BlogLabels } from '../../models/label.model';
import { BlogService } from '../../services/blog/blog.service';
import { TagService } from '../../services/tag/tag.service';
import { Breadcrumbs } from '../../shared/interfaces/breadcrumb.type';
import { RouterService } from '../../shared/services/router.service';

@Component({
    selector: 'blog-article',
    templateUrl: './article.component.html',
    styleUrls: ['./article.component.scss'],
    imports: [
        CommonModule,
        HeroPictureComponent,
        FsAddSectionComponent,
        ContentComponent,
        ConnectedArticleListComponent,
        AsyncPipe,
    ],
})
export class ArticleComponent extends OnDestroyBase implements OnInit, OnDestroy {
    public env = environment;

    public article?: BlogPost | null;
    public breakpoint: CustomBreakpoint = 0;

    public relatedPosts: BlogPost[] = [];
    public labels?: Observable<BlogLabels>;
    public breadcrumbs: Breadcrumbs = [];

    public constructor(
        private routerService: RouterService,
        private rout: Router,
        private breakpointObserver: BreakpointObserver,
        private tagService: TagService,
        private blogService: BlogService,
        private configService: HeaderConfigService,
        private titleService: Title,
        private fsService: FsService,
        private zone: NgZone,
        private metaService: Meta,
        private trackingService: TrackingService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.trackingService.trackEvent(tracking.step.article.init);

        this.labels = this.blogService.labelsUpdated.pipe(takeUntil(this.destroySubject));
        // language change listener
        this.configService.language.pipe(takeUntil(this.destroySubject)).subscribe(() => {
            if (!this.article) {
                this.init();
            } else {
                this.blogService
                    .getPostById(this.article.page_id)
                    .pipe(takeUntil(this.destroySubject))
                    .subscribe((response) => {
                        this.article = response;
                        this.writeSeoUrlInBrowser();
                        this.initArticle();
                    });
            }
        });

        this.routerService.articleChange.pipe(takeUntil(this.destroySubject)).subscribe((article) => {
            this.article = article;
            this.initArticle();
        });

        if (this.env.preview) {
            this.initOCM();
        }

        this.initBreakpointListener();
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.env.preview) {
            this.fsService.clearPreviewElement();
        }
    }

    public authorClicked(author: Author): void {
        this.trackingService.trackEvent(tracking.step.article.author.clicked);
        this.routerService.navigateAuthor(author);
    }

    public tagClicked(tag: string): void {
        this.trackingService.trackEvent(tracking.step.tag.clicked);
        this.tagService.addTag(tag);
        this.routerService.navigateLandingPage();
    }

    private writeSeoUrlInBrowser(): void {
        if (!this.article) {
            return;
        }
        window.history.pushState('', '', this.article.url);
    }

    private initOCM(): void {
        // we only subscribe to the rerenderView event, because when the contentChange event is
        // triggered it also triggers another rerenderView event reload post by id when the content was changed
        this.fsService.rerenderView.pipe(takeUntil(this.destroySubject)).subscribe(() => {
            if (!this.article) {
                console.error('No article to reload. This should not happen.');

                return;
            }

            // the rerenderView event comes possibly before the data is written into caas
            // therefore we wait 350ms, before reloading the data
            setTimeout(() => {
                this.blogService.getPostById(this.article!.page_id).subscribe((post) => {
                    if (post) {
                        this.updateArticle(post);
                    }
                });
            }, 350);
        });
    }

    private updateArticle(post: BlogPost) {
        this.zone.run(() => {
            this.article = post;
            this.initArticle();
        });
    }

    private init(): void {
        // check if values are available
        if (this.routerService.currentArticle) {
            this.article = this.routerService.currentArticle;
            this.initArticle();
            this.setMetaData();
            this.metaService.removeTag('name=keywords');
        } else {
            // direct entry to article by url
            const url = window.location.pathname;
            if (!url) {
                this.routerService.navigateLandingPage();

                return;
            }
            this.loadPostFromBackend(url);
            const metaInterval = setInterval(() => {
                if (this.metaService.getTag('name=description') !== null) {
                    this.setMetaData();
                    clearInterval(metaInterval);
                }
            }, 500);
        }
    }

    private setMetaData() {
        this.metaService.removeTag('name=keywords');
    }

    private loadPostFromBackend(url: string): void {
        this.blogService.getPostBySeoUrl(url).subscribe((post: BlogPost | null) => {
            // can be null, if article was deleted
            if (post) {
                this.updateArticle(post);
            } else {
                this.zone.run(() => this.routerService.navigateLandingPage());
            }
        });
    }

    private initArticle(): void {
        if (!this.article) {
            return;
        }

        if (this.env.preview) {
            this.fsService.setPreviewElement(this.article.node_preview_id);
        }

        if (this.article.meta_data && this.article.meta_data.pageTitle) {
            this.titleService.setTitle(this.article.meta_data.pageTitle);
            this.metaService.updateTag({
                name: 'description',
                content: this.article.meta_data.description ?? '',
            });
        }

        this.labels?.subscribe((labels) => {
            const articleHeadline = this.article ? this.article.headline : '';
            this.breadcrumbs = getBreadcrumbs(labels, articleHeadline, environment.preview, this.fsService);
        });

        // get related posts
        if (this.article.related_posts.length) {
            this.blogService
                .getPostsById(this.article.related_posts)
                .pipe(takeUntil(this.destroySubject))
                .subscribe((response) => {
                    this.relatedPosts = response;
                    // force router to reload page
                    this.rout.routeReuseStrategy.shouldReuseRoute = () => false;
                });
        }
    }

    private initBreakpointListener(): void {
        this.breakpointObserver
            .observe([mediaQueries.mq1, mediaQueries.mq2, mediaQueries.mq3])
            .pipe(takeUntil(this.destroySubject))
            .subscribe((state) => {
                this.breakpoint = getCustomBreakpoint(state);
            });
    }
}
